import { useCallback } from "react";

import { ChevronRight } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useAnalytics, { AnalyticsCommonProps } from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { ThemeContext } from "views/Theme";

import { CollapsibleSectionContext } from "./Context";
import styles from "./styles.module.css";
import { CollapsibleSectionAriaLevel } from "./types";

export type CollapsibleSectionToggleProps = AnalyticsCommonProps & {
  ariaLevel: CollapsibleSectionAriaLevel;
} & ({ ariaLabel: string; ariaLabelledby?: never } | { ariaLabel?: never; ariaLabelledby: string });

function CollapsibleSectionToggle({
  analyticsPage,
  analyticsTitle,
  analyticsProps,
  ariaLevel,
  ariaLabelledby,
  ariaLabel,
}: CollapsibleSectionToggleProps) {
  const { onToggle, isCollapsed, contentId } = useTypedContext(CollapsibleSectionContext);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const trackAnalytics = useCallback(() => {
    if (analyticsTitle) {
      trackSegmentAnalyticsEvent?.(analyticsTitle, { ...analyticsProps, visible: !isCollapsed });
    }
  }, [analyticsTitle, analyticsProps, trackSegmentAnalyticsEvent, isCollapsed]);

  const handleClickToggle = useCallback(() => {
    onToggle();
    trackAnalytics();
  }, [onToggle, trackAnalytics]);

  const { reducedMotion } = useTypedContext(ThemeContext);

  return (
    <Box align="center" role="heading" aria-level={ariaLevel} className={styles.toggleWrapper}>
      <ButtonIcon
        onPress={handleClickToggle}
        variant="ghost"
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
        icon={ChevronRight}
        iconRotate={isCollapsed ? undefined : "90"}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        className={!reducedMotion ? styles.toggle : undefined}
      >
        {isCollapsed ? "Expand" : "Collapse"}
      </ButtonIcon>
    </Box>
  );
}

CollapsibleSectionToggle.displayName = "DS.CollapsibleSection.Toggle";
export default CollapsibleSectionToggle;
